import { createContext, useContext } from 'react';
import { observer, useLocalObservable } from 'mobx-react';
import { useAgentSessionList } from '../hooks';

const AgentSearchContext = createContext();

export const useAgentSearch = () => {
  const context = useContext(AgentSearchContext);
  if (!context) {
    throw new Error('useAgentSearch must be used within AgentSearchProvider');
  }
  return context;
};

export const AgentSearchProvider = observer(({ children }) => {
  // Get search functionality from hook
  const { searchSessions, allSessions } = useAgentSessionList();

  const searchState = useLocalObservable(() => ({
    // Modal state
    isModalOpen: false,
    modalType: null, // 'chat' | 'project'

    // Search state
    searchQuery: '',
    results: [],
    isLoading: false,

    // Modal actions
    openModal: (type) => {
      searchState.modalType = type;
      searchState.isModalOpen = true;
      searchState.searchQuery = '';
      searchState.results = searchState.getDefaultResults();
      // Auto-focus search input after modal opens
      setTimeout(() => {
        const input = document.querySelector('.agent-search-input input');
        if (input) input.focus();
      }, 100);
    },

    closeModal: () => {
      searchState.isModalOpen = false;
      searchState.searchQuery = '';
      searchState.results = [];
      searchState.modalType = null;
    },

    // Search actions
    setSearchQuery: (query) => {
      searchState.searchQuery = query;
      searchState.performSearch();
    },

    performSearch: async () => {
      // Debounced search implementation
      if (searchState.searchTimeout) {
        clearTimeout(searchState.searchTimeout);
      }

      searchState.searchTimeout = setTimeout(async () => {
        if (!searchState.searchQuery.trim()) {
          searchState.results = searchState.getDefaultResults();
          return;
        }

        // Only search chats for now (projects search not implemented yet)
        if (searchState.modalType !== 'chat') {
          searchState.results = searchState.getMockResults();
          return;
        }

        searchState.isLoading = true;

        try {
          // NOTE: Backend full-text search capability exists here but is no longer used
          // The popup modal that used this search was replaced by /conversations page
          // which only does client-side title filtering
          // Backend search API: searchSessions(query) - searches both titles and message content
          // Current page search: Client-side title-only filtering in AgentConversationListView.jsx

          // Call backend search API (searches conversation texts, not just titles)
          if (import.meta.env.DEV) {
            console.log('🔍 SEARCH: Calling searchSessions with query:', searchState.searchQuery);
          }
          const searchResults = await searchSessions(searchState.searchQuery);
          if (import.meta.env.DEV) {
            console.log('🔍 SEARCH: Received results from searchSessions:', searchResults);
            console.log('🔍 SEARCH: Result count:', searchResults?.length || 0);
            if (searchResults && searchResults.length > 0) {
              console.log('🔍 SEARCH: First result structure:', searchResults[0]);
            }
          }

          // Transform session results to match UI format
          // Note: searchSessions returns transformed session objects, not raw backend results
          searchState.results = searchResults.map((result) => ({
            id: result.id, // Session ID
            title: result.title, // Session title
            excerpt: result.matchText || result.title, // Use matched text if available
            timestamp: result.lastActivity || result.createdAt, // Use session timestamp
            type: 'chat',
            similarityScore: result.searchScore, // Renamed from similarityScore
            matchText: result.matchText // Highlighted match text
          }));

          if (import.meta.env.DEV) {
            console.log('🔍 SEARCH: Transformed results:', searchState.results);
            console.log('🔍 SEARCH: Transformed count:', searchState.results.length);
          }
          searchState.isLoading = false;
        } catch (error) {
          console.error('❌ Search failed:', error);
          // Fallback to client-side title search on backend error
          const query = searchState.searchQuery.toLowerCase();
          const clientResults = (allSessions || [])
            .filter((s) => s.title?.toLowerCase().includes(query))
            .sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity))
            .slice(0, 20);

          searchState.results = clientResults.map((session) => ({
            id: session.id,
            title: session.title,
            excerpt: `Last updated: ${new Date(session.lastActivity).toLocaleString()}`,
            timestamp: session.lastActivity || session.createdAt,
            type: 'chat'
          }));
          searchState.isLoading = false;
        }
      }, 300); // Debounce backend search calls
    },

    // Get default results from actual sessions
    getDefaultResults: () => {
      if (searchState.modalType === 'chat') {
        // Use actual sessions from the hook
        return (allSessions || []).slice(0, 20).map((session) => ({
          id: session.id,
          title: session.title,
          excerpt: `Last updated: ${new Date(session.lastActivity).toLocaleString()}`,
          timestamp: new Date(session.lastActivity),
          type: 'chat'
        }));
      } else {
        // Project search still uses mock data
        return [
          // Today - multiple projects
          {
            id: 1,
            title: 'Q4 2024 PE Deal Pipeline',
            excerpt: 'Private equity opportunities in tech sector, €100M+ deals',
            timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000), // 3 hours ago
            type: 'project'
          },
          {
            id: 2,
            title: 'FinTech Acquisitions Q4',
            excerpt: 'Payment processors and digital banking platforms',
            timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000), // 5 hours ago
            type: 'project'
          },
          // Yesterday - multiple projects
          {
            id: 3,
            title: 'European SaaS Companies',
            excerpt: 'B2B software companies, €10M+ revenue, Series B+',
            timestamp: new Date(Date.now() - 20 * 60 * 60 * 1000), // Yesterday
            type: 'project'
          },
          {
            id: 4,
            title: 'Healthcare Tech M&A',
            excerpt: 'Digital health platforms and medical device companies',
            timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000), // Yesterday
            type: 'project'
          },
          {
            id: 5,
            title: 'Clean Energy Investments',
            excerpt: 'Solar, wind, and energy storage companies in DACH region',
            timestamp: new Date(Date.now() - 26 * 60 * 60 * 1000), // Yesterday
            type: 'project'
          },
          // Last Week - multiple projects
          {
            id: 6,
            title: 'German Manufacturing Roll-up',
            excerpt: 'Industrial automation and precision engineering targets',
            timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
            type: 'project'
          },
          {
            id: 7,
            title: 'Nordic Tech Scouting',
            excerpt: 'Scandinavian B2B software and gaming companies',
            timestamp: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000), // 4 days ago
            type: 'project'
          },
          {
            id: 8,
            title: 'AI/ML Infrastructure Play',
            excerpt: 'Companies providing AI tools and infrastructure services',
            timestamp: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000), // 6 days ago
            type: 'project'
          },
          // Last Month - multiple projects
          {
            id: 9,
            title: 'Food Tech Investment Theme',
            excerpt: 'Alternative proteins and food sustainability solutions',
            timestamp: new Date(Date.now() - 16 * 24 * 60 * 60 * 1000), // 2+ weeks ago
            type: 'project'
          },
          {
            id: 10,
            title: 'Cybersecurity Consolidation',
            excerpt: 'SMB-focused security software companies in Europe',
            timestamp: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000), // 3 weeks ago
            type: 'project'
          },
          {
            id: 11,
            title: 'Logistics Tech Platform',
            excerpt: 'Last-mile delivery and supply chain optimization',
            timestamp: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000), // 3.5 weeks ago
            type: 'project'
          },
          // Older - multiple projects
          {
            id: 12,
            title: 'Real Estate PropTech',
            excerpt: 'Commercial real estate management and analytics platforms',
            timestamp: new Date(Date.now() - 38 * 24 * 60 * 60 * 1000), // 5+ weeks ago
            type: 'project'
          },
          {
            id: 13,
            title: 'EdTech Corporate Training',
            excerpt: 'B2B learning platforms and skills development solutions',
            timestamp: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000), // 6+ weeks ago
            type: 'project'
          }
        ];
      }
    },

    getMockResults: () => {
      // Filter default results based on search query
      return searchState
        .getDefaultResults()
        .filter(
          (item) =>
            item.title.toLowerCase().includes(searchState.searchQuery.toLowerCase()) ||
            item.excerpt.toLowerCase().includes(searchState.searchQuery.toLowerCase())
        );
    },

    // Grouped results for time-based display
    get groupedResults() {
      const groups = {};
      const now = new Date();

      searchState.results.forEach((result) => {
        const key = searchState.getTimeGroupKey(result.timestamp, now);
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(result);
      });

      return groups;
    },

    getTimeGroupKey: (timestamp, now) => {
      const diffDays = Math.floor((now - timestamp) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays <= 7) return 'Last Week';
      if (diffDays <= 30) return 'Last Month';
      return 'Older';
    }
  }));

  // Note: Default results are set in openModal() action - no initialization needed here
  // Removed buggy initialization that caused MobX strict-mode violations

  return <AgentSearchContext.Provider value={searchState}>{children}</AgentSearchContext.Provider>;
});

export default AgentSearchProvider;
